<template lang="pug">
.add-courses-container
	.top-bar
		.row.top
			.icon.icon-toggle( @click='toggleMenu' )
			profile-image(
				class="profile-image"
				@profile-click="openUser"
				@feedback-click="giveFeedback"
			)
			.headers
				h2.year-pretitle {{ sectionInfo.year }} Year
				h1.subject-header {{ section.label }} Courses
		p.description Click “Learn More” to view course details. Click anywhere else on the card to add it to Your Plan. When you’re done, click “Done”

	.course-cards-wrapper
		.course-cards
			CourseCard.course-card(
				v-for="(ci, i) in courseInfos"
				:key="i"
				:course="ci.course"
				:saved="isSaved(ci.course.id)"
				:planned="isPlanned(ci.course.id)"
				:enablePlanning="true"
				@save="saveCourse(ci.course)"
				@unsave="unsaveCourse(ci.course)"
				@plan="planCourse(ci.course)"
				@unplan="unplanCourse(ci.course)"
				@view="viewCourse(ci.course)"
			)
	.footer
		.button-glow.button-done(@click="$emit('done')") Done
</template>

<script>
import SearchBar from '@/components/SearchBar';
import ProfileImage from '@/components/ProfileImage';
import CourseCard from '@/components/CourseCard';

export default {
  name       : 'your-plan-add-courses',
  components : {
    SearchBar,
    ProfileImage,
    CourseCard
  },

  props : {
    /**
		 * Contains information regarding the specified year (freshman, sophomore, etc)
		 * and the index to the section in the studentPlan object.
		 *
		 * Format:
		 * {
		 * 		year: string;
		 * 		index: number;
		 * }
		 */
    sectionInfo : Object
  },

  data() {
    return {
      courseInfos : []
    };
  },

  computed : {
    section() {
      const { year, index } = this.sectionInfo;
      return this.$store.getters.studentPlan[year][index];
    },

    savedCourseKeys() {
      return this.$store.getters.savedCourseKeys;
    },
  },

  created() {
    /**
		 * Gets the courses that fall under this.sectionInfo.section.gradCredit
		 * Planned courses are prioritized first, then saved courses, then everything else.
		 *
		 * Note the special edge case for CTE courses, where we filter by chosen program instead
		 * of just showing all CTE courses.
		 */
    const { gradCredits } = this.section;
    let courses = [];
    // console.log( gradCredits );
    gradCredits.forEach( ( gc ) => {
      courses = courses.concat(
        this.$store.getters.getCoursesByGradCredit( gc )
        // filter applies only to CTE courses, as we filter by chosenProgram instead
          .filter( ( course ) => {
            if ( gc === '-CTE' ) {
              return course.ctePathways && course.ctePathways.includes( this.$store.getters.chosenProgram );
            }
            return true;
          } )
          .map( course => ( {
            course,
            saved   : course.id in this.savedCourseKeys,
            planned : this.section.courses ? this.section.courses.includes( course.id ) : false
          } ) )
      );
    } );

    // Show planned first, then saved, then everything else. Should otherwise be ordered according
    // to the gradCredits order for the section
    courses.sort( ( c1, c2 ) => {
      if ( c1.planned ) return -1;
      if ( c2.planned ) return 1;
      if ( c1.saved ) return -1;
      if ( c2.saved ) return 1;
      return 1;
    } );

    // Sections with multiple gradCredits may have overlapping courses, so filter out any duplicates
    const uniq = ( arr ) => {
      const seen = {};
      return arr.filter( ( { course } ) => {
        if ( seen.hasOwnProperty( course.id ) ) return false;
        seen[course.id] = true;
        return true;
      } );
    };
    this.courseInfos = uniq( courses );
  },


  methods : {
    openUser() {
      this.$store.dispatch( 'updateStore', ['userSwiperState', 'open'] );
    },
    toggleMenu() {
      this.$store.dispatch( 'toggleSideMenu' );
    },
    giveFeedback() {
      // eslint-disable-next-line no-undef
      SpacebarFeedback.open();
    },
    isPlanned( courseID ) {
      return this.section.courses ? this.section.courses.includes( courseID ) : false;
    },
    isSaved( courseID ) {
      return courseID in this.savedCourseKeys;
    },

    viewCourse( course ) {
      this.$store.dispatch( 'openCourseInfo', course );
    },

    saveCourse( course ) {
      this.$store.dispatch( 'saveCourse', course.id );
    },

    unsaveCourse( course ) {
      this.$store.dispatch( 'unsaveCourse', course.id );
    },

    planCourse( course ) {
      this.$store.dispatch( 'planCourse', {
        year       : this.sectionInfo.year,
        index      : this.sectionInfo.index,
        courseID   : course.id,
        saveCourse : true
      } );
    },

    unplanCourse( course ) {
      this.$store.dispatch( 'unplanCourse', {
        year     : this.sectionInfo.year,
        index    : this.sectionInfo.index,
        courseID : course.id
      } );
    }
  }
};
</script>

<style lang="scss" scoped>
.add-courses-container {
	position: relative;
	height: 100vh;
	display: flex;
	flex-direction: column;

	.footer {
		position: absolute;
		bottom: 0px;
		left: 0px;
		width: 100%;
		padding: 20px 0;
		background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 80.48%);
		display: flex;
		justify-content: center;
		align-items: center;
	}

	.course-cards-wrapper {
		flex: 1;
		overflow: auto;
		padding-bottom: 80px;
		.course-cards {
			padding: 20px;
			display: grid;
			grid-template-columns: repeat(1, 1fr);
			gap: 20px;

			@include md {
				margin-top: 20px;
				grid-template-columns: repeat(2, 1fr);
			}

			@include xl {
				grid-template-columns: repeat(3, 1fr);
			}
		}
	}

	.top-bar {
		position: relative;
		min-height: 130px;
		z-index: 1;
		box-shadow: $floatingObject;
		.profile-image {
			position: absolute;
			top: 10px;
			right: 10px;
		}
		.top {
			padding: 10px;
			position: relative;
			background: white;
			z-index: 10;
			display: flex;
			align-items: center;
			justify-content: center;
			.icon-toggle {
				left: 15px;
				position: absolute;
			}
			.headers {
				height: 100%;
				text-align: center;
				color: $primary;
				font-weight: 600;
				margin-top: 8px;
				.year-pretitle {
					text-transform: uppercase;
					font-size: 14px;
					letter-spacing: 2px;
				}
				.subject-header {
					font-size: 24px;
					font-weight: 600;
				}
			}
			.icon-person {
				right: 0;
				padding: 25px;
			}
		}

		.description {
			text-align: center;
			margin: 0 auto;
			max-width: 800px;
			padding: 10px 10px;
			padding-top: 0;
			font-size: 16px;
		}
	}
}

@media only screen and ( min-width : 993px ) {
	.top-bar {
		.top {
			.icon-toggle {
				opacity: 0;
				pointer-events: none;
			}
		}
	}
}
</style>
